"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import { toast } from "sonner";
import {
  Building2,
  Mail,
  Phone,
  User,
  FileText,
  Users,
  MessageSquare,
  Globe,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type TrialApplicationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planCode: string;
  planTier: string;
  planName: string;
  applicationType: "AUTO" | "ELIGIBILITY" | "SALES";
  applicationId?: string;
  onSuccess?: () => void;
};

export function TrialApplicationDialog({
  open,
  onOpenChange,
  planCode,
  planTier,
  planName,
  applicationType,
  applicationId: propApplicationId,
  onSuccess,
}: TrialApplicationDialogProps) {
  const t = useTranslations("billingPage");
  const locale = useLocale();
  const [loading, setLoading] = useState(false);
  // Enterprise 邮箱验证状态
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // 检测 URL 参数中的邮箱验证状态，按 applicationId 精准回填
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (applicationType !== "SALES") return;

    const params = new URLSearchParams(window.location.search);
    const emailVerifiedParam = params.get("emailVerified");
    const applicationId = propApplicationId || params.get("applicationId");
    const verifiedEmail = params.get("verifiedEmail");

    if (emailVerifiedParam !== "true") return;

    console.log(
      "[TrialApplicationDialog] Email verified detected. applicationId:",
      applicationId,
      "verifiedEmail:",
      verifiedEmail,
    );

    setEmailVerified(true);

    // 按 applicationId 精准加载草稿
    if (applicationId) {
      fetch(`/api/trial-applications/${applicationId}`)
        .then((res) => {
          if (!res.ok) {
            console.warn(
              "[TrialApplicationDialog] Failed to load draft by applicationId:",
              res.status,
            );
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (data?.application) {
            const draft = data.application;
            console.log("[TrialApplicationDialog] Draft loaded by applicationId:", draft.id);
            setFormData({
              companyName: draft.companyName || "",
              companySize: draft.companySize || "",
              contactName: draft.contactName || "",
              contactEmail: draft.contactEmail || verifiedEmail || "",
              contactPhone: draft.contactPhone || "",
              useCase: draft.useCase || "",
              expectedSeats: draft.expectedSeats?.toString() || "",
              billingCycle: draft.billingCycle || "monthly",
              documentUrl: draft.documentUrl || "",
              companyWebsite: draft.companyWebsite || "",
              customRequirements: draft.customRequirements || "",
              demoRequested: draft.demoRequested || false,
            });
          } else {
            // applicationId 无效或记录不存在，只展示邮箱已验证状态
            console.warn("[TrialApplicationDialog] applicationId not found, showing email verified only");
            if (verifiedEmail) {
              setFormData((prev) => ({ ...prev, contactEmail: verifiedEmail }));
            }
          }
        })
        .catch((error) => {
          console.error("[TrialApplicationDialog] Error loading draft:", error);
          if (verifiedEmail) {
            setFormData((prev) => ({ ...prev, contactEmail: verifiedEmail }));
          }
        })
        .finally(() => {
          // 清除 URL 参数（在加载完成后）
          const url = new URL(window.location.href);
          url.searchParams.delete("emailVerified");
          url.searchParams.delete("verifiedEmail");
          url.searchParams.delete("applicationId");
          window.history.replaceState({}, "", url.toString());
        });
    } else {
      // 无 applicationId，只展示邮箱已验证状态，不回填旧草稿
      console.warn("[TrialApplicationDialog] No applicationId, showing email verified only");
      if (verifiedEmail) {
        setFormData((prev) => ({ ...prev, contactEmail: verifiedEmail }));
      }
      // 清除 URL 参数
      const url = new URL(window.location.href);
      url.searchParams.delete("emailVerified");
      url.searchParams.delete("verifiedEmail");
      url.searchParams.delete("applicationId");
      window.history.replaceState({}, "", url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propApplicationId]);

  const [formData, setFormData] = useState({
    companyName: "",
    companySize: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    useCase: "",
    expectedSeats: "",
    billingCycle: "monthly",
    documentUrl: "",
    companyWebsite: "",
    customRequirements: "",
    demoRequested: false,
  });

  const handleSendVerification = async () => {
    if (!formData.contactName.trim()) {
      toast.error(t("trialApplication.contactNameRequired"));
      return;
    }
    if (!formData.contactEmail.trim()) {
      toast.error(t("trialApplication.contactEmailRequired"));
      return;
    }
    if (!formData.companyWebsite.trim()) {
      toast.error(t("trialApplication.companyWebsiteRequired"));
      return;
    }

    setVerificationLoading(true);
    try {
      const res = await fetch("/api/trial-applications/send-email-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planCode,
          planTier,
          contactEmail: formData.contactEmail,
          contactName: formData.contactName,
          companyName: formData.companyName,
          companySize: formData.companySize,
          companyWebsite: formData.companyWebsite,
          contactPhone: formData.contactPhone,
          useCase: formData.useCase,
          expectedSeats: formData.expectedSeats,
          billingCycle: formData.billingCycle,
          documentUrl: formData.documentUrl,
          customRequirements: formData.customRequirements,
          demoRequested: formData.demoRequested,
          locale,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? t("trialApplication.verificationFailed"));
      }
      setVerificationSent(true);
      toast.success(t("trialApplication.verificationSent"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("trialApplication.verificationFailed"));
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleSubmit = async () => {
    // 验证必填字段（所有类型）
    if (!formData.contactName.trim()) {
      toast.error(t("trialApplication.contactNameRequired"));
      return;
    }
    if (!formData.contactEmail.trim()) {
      toast.error(t("trialApplication.contactEmailRequired"));
      return;
    }

    // Education/Startup 需要公司名称、使用场景、证明文件、预期席位数、公司规模
    if (applicationType === "ELIGIBILITY") {
      if (!formData.companyName.trim()) {
        toast.error(t("trialApplication.companyNameRequired"));
        return;
      }
      if (!formData.useCase.trim()) {
        toast.error(t("trialApplication.useCaseRequired"));
        return;
      }
      if (!formData.documentUrl.trim()) {
        toast.error(t("trialApplication.documentRequired"));
        return;
      }
      if (!formData.expectedSeats.trim()) {
        toast.error(t("trialApplication.expectedSeatsRequired"));
        return;
      }
      if (!formData.companySize.trim()) {
        toast.error(t("trialApplication.companySizeRequired"));
        return;
      }
    }

    // Enterprise 需要公司名称、公司邮箱、公司网站、使用场景、联系电话、预期席位数、公司规模
    if (applicationType === "SALES") {
      if (!emailVerified) {
        toast.error(t("trialApplication.emailVerificationRequired"));
        return;
      }
      if (!formData.companyName.trim()) {
        toast.error(t("trialApplication.companyNameRequired"));
        return;
      }
      if (!formData.useCase.trim()) {
        toast.error(t("trialApplication.useCaseRequired"));
        return;
      }
      if (!formData.contactPhone.trim()) {
        toast.error(t("trialApplication.contactPhoneRequired"));
        return;
      }
      if (!formData.expectedSeats.trim()) {
        toast.error(t("trialApplication.expectedSeatsRequired"));
        return;
      }
      if (!formData.companySize.trim()) {
        toast.error(t("trialApplication.companySizeRequired"));
        return;
      }
      // 验证公司邮箱（必须使用公司邮箱，不能是免费邮箱）
      const email = formData.contactEmail.trim().toLowerCase();
      const freeEmailDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "qq.com", "163.com", "126.com", "sina.com"];
      const emailDomain = email.split("@")[1];
      if (freeEmailDomains.includes(emailDomain)) {
        toast.error(t("trialApplication.companyEmailRequired"));
        return;
      }
      // 验证公司网站
      if (!formData.companyWebsite.trim()) {
        toast.error(t("trialApplication.companyWebsiteRequired"));
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch("/api/trial-applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planCode,
          planTier,
          ...formData,
          expectedSeats: formData.expectedSeats
            ? parseInt(formData.expectedSeats)
            : undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error);
      }

      // 根据消息类型显示对应的翻译
      const successMessage =
        data.messageType === "autoApproved"
          ? t("trialApplication.autoApprovedSuccess")
          : t("trialApplication.submittedSuccess");
      toast.success(successMessage);
      onOpenChange(false);
      onSuccess?.();

      // 重置表单
      setFormData({
        companyName: "",
        companySize: "",
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        useCase: "",
        expectedSeats: "",
        billingCycle: "monthly",
        documentUrl: "",
        companyWebsite: "",
        customRequirements: "",
        demoRequested: false,
      });
      setEmailVerified(false);
      setVerificationSent(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("trialApplication.submitFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getDialogTitle = () => {
    switch (applicationType) {
      case "AUTO":
        return t("trialApplication.startTrialTitle");
      case "ELIGIBILITY":
        return t("trialApplication.applyTrialTitle");
      case "SALES":
        return t("trialApplication.contactSalesTitle");
      default:
        return t("trialApplication.startTrialTitle");
    }
  };

  const getDialogDescription = () => {
    switch (applicationType) {
      case "AUTO":
        return t("trialApplication.autoDescription", { planName });
      case "ELIGIBILITY":
        return t("trialApplication.eligibilityDescription", { planName });
      case "SALES":
        return t("trialApplication.salesDescription", { planName });
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 联系人信息 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {t("trialApplication.contactName")} *
              </Label>
              <Input
                placeholder={t("trialApplication.contactNamePlaceholder")}
                value={formData.contactName}
                onChange={(e) =>
                  setFormData({ ...formData, contactName: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {t("trialApplication.contactEmail")} *
              </Label>
              <Input
                type="email"
                placeholder={t("trialApplication.contactEmailPlaceholder")}
                value={formData.contactEmail}
                onChange={(e) =>
                  setFormData({ ...formData, contactEmail: e.target.value })
                }
              />
              {/* Enterprise 套餐公司邮箱提示 */}
              {applicationType === "SALES" && (
                <p className="text-subtle text-sm">
                  {t("trialApplication.businessEmailHint")}
                </p>
              )}
              {/* Enterprise 邮箱验证按钮 */}
              {applicationType === "SALES" && (
                <div className="flex items-center gap-2 mt-1">
                  {emailVerified ? (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {t("trialApplication.emailVerifiedBadge")}
                    </span>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs px-2"
                        disabled={verificationLoading || !formData.contactEmail || !formData.companyWebsite}
                        onClick={handleSendVerification}
                      >
                        {verificationLoading ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <ShieldCheck className="w-3 h-3 mr-1" />
                        )}
                        {verificationSent
                          ? t("trialApplication.resendVerification")
                          : t("trialApplication.sendVerification")}
                      </Button>
                      {verificationSent && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          {t("trialApplication.checkInboxHint")}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {t("trialApplication.contactPhone")}
                {applicationType === "SALES" && " *"}
              </Label>
              <Input
                placeholder={t("trialApplication.contactPhonePlaceholder")}
                value={formData.contactPhone}
                onChange={(e) =>
                  setFormData({ ...formData, contactPhone: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {t("trialApplication.expectedSeats")}
                {(applicationType === "ELIGIBILITY" || applicationType === "SALES") && " *"}
              </Label>
              <Input
                type="number"
                placeholder="5"
                value={formData.expectedSeats}
                onChange={(e) =>
                  setFormData({ ...formData, expectedSeats: e.target.value })
                }
              />
              {/* Education/Startup 试用席位提示 */}
              {applicationType === "ELIGIBILITY" && formData.expectedSeats && parseInt(formData.expectedSeats) > 50 && (
                <p className="text-subtle text-sm">
                  {t("trialApplication.seatsHint", { maxSeats: 50 })}
                </p>
              )}
              {/* Enterprise 试用席位提示 */}
              {applicationType === "SALES" && formData.expectedSeats && parseInt(formData.expectedSeats) > 100 && (
                <p className="text-subtle text-sm">
                  {t("trialApplication.enterpriseSeatsHint")}
                </p>
              )}
            </div>
          </div>

          {/* 计费周期意向 */}
          <div className="space-y-2">
            <Label>{t("trialApplication.billingCycle")}</Label>
            <Select
              value={formData.billingCycle}
              onValueChange={(value) =>
                setFormData({ ...formData, billingCycle: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t("trialApplication.billingCyclePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{t("monthly")}</SelectItem>
                <SelectItem value="yearly">{t("yearly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 公司信息 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {t("trialApplication.companyName")}
                {(applicationType === "ELIGIBILITY" || applicationType === "SALES") && " *"}
              </Label>
              <Input
                placeholder={t("trialApplication.companyNamePlaceholder")}
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t("trialApplication.companySize")}
                {(applicationType === "ELIGIBILITY" || applicationType === "SALES") && " *"}
              </Label>
              <Select
                value={formData.companySize}
                onValueChange={(value) =>
                  setFormData({ ...formData, companySize: value })
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("trialApplication.companySizePlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-10">1-10</SelectItem>
                  <SelectItem value="11-50">11-50</SelectItem>
                  <SelectItem value="51-200">51-200</SelectItem>
                  <SelectItem value="201-500">201-500</SelectItem>
                  <SelectItem value="501-1000">501-1000</SelectItem>
                  <SelectItem value="1000+">1000+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Enterprise 需要公司网站 */}
          {applicationType === "SALES" && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Globe className="w-4 h-4" />
                {t("trialApplication.companyWebsite")} *
              </Label>
              <Input
                type="url"
                placeholder="https://www.example.com"
                value={formData.companyWebsite}
                onChange={(e) =>
                  setFormData({ ...formData, companyWebsite: e.target.value })
                }
              />
              <p className="text-subtle text-sm">
                {t("trialApplication.companyWebsiteHint")}
              </p>
            </div>
          )}

          {/* Enterprise 可选证明文件 */}
          {applicationType === "SALES" && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {t("trialApplication.documentUrl")}
              </Label>
              <Input
                placeholder={t("trialApplication.documentUrlPlaceholder")}
                value={formData.documentUrl}
                onChange={(e) =>
                  setFormData({ ...formData, documentUrl: e.target.value })
                }
              />
              <p className="text-subtle text-sm">
                {t("trialApplication.enterpriseDocHint")}
              </p>
            </div>
          )}

          {/* 使用场景 */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {t("trialApplication.useCase")}
              {(applicationType === "ELIGIBILITY" || applicationType === "SALES") && " *"}
            </Label>
            <Textarea
              placeholder={t("trialApplication.useCasePlaceholder")}
              value={formData.useCase}
              onChange={(e) =>
                setFormData({ ...formData, useCase: e.target.value })
              }
              rows={3}
            />
          </div>

          {/* Education/Startup 需要证明文件 */}
          {applicationType === "ELIGIBILITY" && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {t("trialApplication.documentUrl")} *
              </Label>
              <Input
                placeholder={t("trialApplication.documentUrlPlaceholder")}
                value={formData.documentUrl}
                onChange={(e) =>
                  setFormData({ ...formData, documentUrl: e.target.value })
                }
              />
              <p className="text-subtle">
                {planTier === "EDUCATION"
                  ? t("trialApplication.educationDocHint")
                  : t("trialApplication.startupDocHint")}
              </p>
            </div>
          )}

          {/* Enterprise 定制需求 */}
          {applicationType === "SALES" && (
            <div className="space-y-2">
              <Label>{t("trialApplication.customRequirements")}</Label>
              <Textarea
                placeholder={t(
                  "trialApplication.customRequirementsPlaceholder",
                )}
                value={formData.customRequirements}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customRequirements: e.target.value,
                  })
                }
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t("trialApplication.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {loading
              ? t("trialApplication.submitting")
              : t("trialApplication.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
